/**
 * Read-only "incognito" view of a user's account — Super Admin only.
 *
 * Fetches `GET /admin/users/{id}/snapshot` and renders a comprehensive
 * modal with the exact data the target user would see on their own
 * dashboard: profile, accounts, open positions, closed trades, orders,
 * notifications, journal entries. Also lazy-loads a 90-day audit log
 * (logins + IPs + Obondo trades) on tab open, with CSV + print export.
 *
 * The amber READ-ONLY banner reserves right-padding (`pr-36`) so both
 * the built-in Radix close-X and our explicit "Close" button sit above
 * the banner strip without the sticky element clipping them.
 */
import { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogClose,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, API_BASE, fmtKES, fmtPct } from "@/lib/api";
import { Eye, ShieldAlert, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { signTone, toneClassName } from "@/lib/numberColor";
import AuditLogView from "@/components/AuditLogView";
import UserAvatar from "@/components/UserAvatar";
import { Jargon } from "@/components/admin/JargonTerm";
import { useSortableData } from "@/hooks/useSortableData";
import { SortableTh } from "@/components/ui/sortable-th";

function StatusPill({ status }) {
    const s = (status || "active").toLowerCase();
    const cls = {
        active:  "text-success border-success/40 bg-success/10",
        paused:  "text-amber-500 border-amber-500/40 bg-amber-500/10",
        frozen:  "text-danger border-danger/40 bg-danger/10",
        deleted: "text-muted-foreground border-border bg-secondary/40",
    }[s] || "text-muted-foreground border-border bg-secondary/40";
    return (
        <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border rounded ${cls}`}
              data-testid="snapshot-status-pill">
            {s}
        </span>
    );
}

function KV({ label, value, testid }) {
    return (
        <div className="flex justify-between items-baseline gap-4 py-1 border-b border-border/50 text-xs">
            <span className="text-muted-foreground uppercase tracking-wide">{label}</span>
            <span className="mono text-right break-all" data-testid={testid}>{value ?? "—"}</span>
        </div>
    );
}

function EmptyRow({ colSpan, label }) {
    return (
        <tr>
            <td colSpan={colSpan} className="p-6 text-center text-muted-foreground text-xs">
                {label}
            </td>
        </tr>
    );
}

// ─── Main dialog ─────────────────────────────────────────────────────────
export default function UserSnapshotDialog({ userId, open, onClose, apiPath }) {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!open || !userId) return;
        let cancelled = false;
        setLoading(true);
        setData(null);
        const url = apiPath ? apiPath(userId) : `/admin/users/${userId}/snapshot`;
        api.get(url)
            .then((res) => { if (!cancelled) setData(res.data); })
            .catch((e) => toast.error(e.response?.data?.detail || "Failed to load snapshot"))
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, userId, apiPath]);

    const userEmail = data?.user?.original_email || data?.user?.email;
    const { sorted: sortedAccounts, sortKey: accSortKey, direction: accDirection, requestSort: requestAccSort } = useSortableData(data?.accounts, {
        accessors: {
            id: (a) => a.id || "",
            type: (a) => a.type || a.account_type || "",
            balance: (a) => Number(a.current_balance ?? a.balance_kes ?? a.balance ?? 0),
            equity: (a) => Number(a.equity_kes ?? a.equity ?? 0),
            pnl: (a) => Number(a.realized_pnl_kes ?? a.realized_pnl ?? 0),
        },
    });
    const { sorted: sortedOpen, sortKey: openSortKey, direction: openDirection, requestSort: requestOpenSort } = useSortableData(data?.open_positions, {
        accessors: {
            symbol: (p) => p.symbol || "",
            side: (p) => p.side || "",
            qty: (p) => Number(p.qty || 0),
            entry: (p) => Number(p.entry_price || 0),
            opened: (p) => p.entry_ts || "",
        },
    });
    const { sorted: sortedClosed, sortKey: closedSortKey, direction: closedDirection, requestSort: requestClosedSort } = useSortableData(data?.closed_trades, {
        accessors: {
            symbol: (p) => p.symbol || "",
            side: (p) => p.side || "",
            pnl: (p) => Number(p.pnl || 0),
            closed: (p) => p.exit_ts || "",
        },
    });
    const { sorted: sortedOrders, sortKey: orderSortKey, direction: orderDirection, requestSort: requestOrderSort } = useSortableData(data?.recent_orders, {
        accessors: {
            symbol: (o) => o.symbol || "",
            type: (o) => o.order_type || o.type || "",
            side: (o) => o.side || "",
            qty: (o) => Number(o.qty || 0),
            price: (o) => Number(o.price || 0),
            status: (o) => o.status || "",
            ts: (o) => o.ts || "",
        },
    });

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent
                className="max-w-5xl max-h-[90vh] overflow-y-auto p-0"
                data-testid="user-snapshot-dialog"
            >
                {/* Read-only banner — reserves ~144px on the right so the
                    built-in Radix close (top-4 right-4) and our explicit
                    "Close" button both remain clickable above the banner. */}
                <div className="sticky top-0 z-10 bg-amber-500/20 border-b-2 border-amber-500 px-4 py-2 pr-36 flex items-center gap-2 text-amber-700 dark:text-amber-300">
                    <ShieldAlert className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wider">
                        Read-only · Admin incognito view · No actions can be taken
                    </span>
                </div>

                {/* Explicit, always-visible Close button — supplements the
                    Radix X in the top-right corner so a busy super-admin
                    can never miss the exit. */}
                <DialogClose asChild>
                    <button
                        type="button"
                        data-testid="snapshot-close-btn"
                        aria-label="Close snapshot"
                        className="absolute right-14 top-2.5 z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded border border-amber-600/50 bg-white/70 dark:bg-black/40 text-amber-800 dark:text-amber-200 text-[11px] font-semibold hover:bg-amber-500 hover:text-white transition"
                    >
                        <X className="w-3 h-3" /> Close
                    </button>
                </DialogClose>

                <div className="p-6">
                    <DialogHeader className="mb-4">
                        <DialogTitle className="flex items-center gap-2">
                            <Eye className="w-4 h-4" />
                            Account snapshot
                        </DialogTitle>
                        <DialogDescription className="text-xs">
                            Read-only projection of everything the user sees on their own dashboard — profile,
                            trading accounts, positions, orders, notifications, journal entries, and a
                            90-day audit log.
                        </DialogDescription>
                    </DialogHeader>

                    {loading && (
                        <div className="flex items-center justify-center py-16 text-muted-foreground" data-testid="snapshot-loading">
                            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading snapshot…
                        </div>
                    )}

                    {!loading && data && (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                <div className="border border-border rounded p-3 bg-card">
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Profile</div>
                                    <div className="flex items-center gap-3 mb-3 pb-2 border-b border-border/50">
                                        <UserAvatar user={data.user} size={56} testid="snap-avatar" />
                                        <div className="min-w-0">
                                            <div className="font-semibold truncate">{data.user?.full_name}</div>
                                            <div className="text-[10px] text-muted-foreground truncate">{userEmail}</div>
                                        </div>
                                    </div>
                                    <KV label="Full name" value={data.user?.full_name} testid="snap-name" />
                                    <KV label="Email" value={userEmail} testid="snap-email" />
                                    <KV label="Phone" value={data.user?.phone} testid="snap-phone" />
                                    <KV label="Tier" value={(data.user?.tier || "individual").toUpperCase()} testid="snap-tier" />
                                    <KV label="Role" value={data.user?.role} testid="snap-role" />
                                    <KV
                                        label="Status"
                                        value={<StatusPill status={data.summary?.account_status} />}
                                        testid="snap-status"
                                    />
                                    <KV label={<Jargon term="kyc" />} value={data.user?.kyc_status || "not_started"} testid="snap-kyc" />
                                    <KV label="Recovery codes issued" value={data.user?.recovery_codes_issued ? "yes" : "no"} testid="snap-rc" />
                                </div>
                                <div className="border border-border rounded p-3 bg-card">
                                    <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Trading summary (recent)</div>
                                    <KV label="Open positions" value={data.summary?.open_positions_count} testid="snap-open-count" />
                                    <KV label="Closed trades (last 20)" value={data.summary?.recent_closed_count} testid="snap-closed-count" />
                                    <KV label={<>Recent <Jargon term="pnl" /></>} value={fmtKES(data.summary?.recent_pnl_kes)} testid="snap-recent-pnl" />
                                    <KV label="Recent win rate" value={fmtPct(data.summary?.recent_win_rate_pct)} testid="snap-recent-winrate" />
                                    <KV label="Notifications" value={data.notifications?.length ?? 0} testid="snap-notif-count" />
                                    <KV label="Journal entries" value={data.journal_entries?.length ?? 0} testid="snap-journal-count" />
                                    <KV label="Must change password" value={data.summary?.must_change_password ? "yes" : "no"} testid="snap-mcp" />
                                    <KV label="Snapshot generated" value={new Date(data.generated_at).toLocaleString("en-KE")} testid="snap-generated" />
                                </div>
                            </div>

                            <Tabs defaultValue="accounts">
                                <TabsList className="mb-3 flex-wrap">
                                    <TabsTrigger value="accounts" data-testid="snap-tab-accounts">Accounts</TabsTrigger>
                                    <TabsTrigger value="open" data-testid="snap-tab-open">Open positions</TabsTrigger>
                                    <TabsTrigger value="closed" data-testid="snap-tab-closed">Closed trades</TabsTrigger>
                                    <TabsTrigger value="orders" data-testid="snap-tab-orders">Orders</TabsTrigger>
                                    <TabsTrigger value="notifications" data-testid="snap-tab-notifications">Notifications</TabsTrigger>
                                    <TabsTrigger value="journal" data-testid="snap-tab-journal">Journal</TabsTrigger>
                                    <TabsTrigger value="audit" data-testid="snap-tab-audit">Audit log</TabsTrigger>
                                </TabsList>

                                <TabsContent value="accounts">
                                    <div className="border border-border rounded overflow-hidden">
                                        <table className="w-full text-xs">
                                            <thead className="bg-secondary/30 text-muted-foreground">
                                                <tr>
                                                    <SortableTh label="Account ID" sortKey="id" currentKey={accSortKey} direction={accDirection} onSort={requestAccSort} className="px-3 py-2" />
                                                    <SortableTh label="Type" sortKey="type" currentKey={accSortKey} direction={accDirection} onSort={requestAccSort} className="px-3 py-2" />
                                                    <SortableTh label="Balance" sortKey="balance" currentKey={accSortKey} direction={accDirection} onSort={requestAccSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label="Equity" sortKey="equity" currentKey={accSortKey} direction={accDirection} onSort={requestAccSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label={<>Realized <Jargon term="pnl" /></>} sortKey="pnl" currentKey={accSortKey} direction={accDirection} onSort={requestAccSort} align="right" className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedAccounts.map((a) => (
                                                    <tr key={a.id} className="border-t border-border">
                                                        <td className="px-3 py-2 mono">{a.id?.slice(-8)}</td>
                                                        <td className="px-3 py-2 uppercase">{a.type || a.account_type || "—"}</td>
                                                        <td className="px-3 py-2 text-right mono">{fmtKES(a.current_balance ?? a.balance_kes ?? a.balance)}</td>
                                                        <td className="px-3 py-2 text-right mono">{fmtKES(a.equity_kes ?? a.equity)}</td>
                                                        <td className="px-3 py-2 text-right mono">{fmtKES(a.realized_pnl_kes ?? a.realized_pnl)}</td>
                                                    </tr>
                                                ))}
                                                {(!data.accounts || data.accounts.length === 0) && (
                                                    <EmptyRow colSpan={5} label="No trading accounts." />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </TabsContent>

                                <TabsContent value="open">
                                    <div className="border border-border rounded overflow-hidden max-h-[400px] overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-secondary/30 text-muted-foreground sticky top-0">
                                                <tr>
                                                    <SortableTh label="Symbol" sortKey="symbol" currentKey={openSortKey} direction={openDirection} onSort={requestOpenSort} className="px-3 py-2" />
                                                    <SortableTh label="Side" sortKey="side" currentKey={openSortKey} direction={openDirection} onSort={requestOpenSort} className="px-3 py-2" />
                                                    <SortableTh label="Qty" sortKey="qty" currentKey={openSortKey} direction={openDirection} onSort={requestOpenSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label="Entry" sortKey="entry" currentKey={openSortKey} direction={openDirection} onSort={requestOpenSort} align="right" className="px-3 py-2" />
                                                    <th className="text-right px-3 py-2">SL / TP</th>
                                                    <SortableTh label="Opened" sortKey="opened" currentKey={openSortKey} direction={openDirection} onSort={requestOpenSort} className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedOpen.map((p) => (
                                                    <tr key={p.id} className="border-t border-border">
                                                        <td className="px-3 py-2 mono font-semibold">{p.symbol}</td>
                                                        <td className={`px-3 py-2 uppercase font-bold ${p.side === "buy" ? "text-success" : "text-danger"}`}>{p.side}</td>
                                                        <td className="px-3 py-2 text-right mono">{p.qty}</td>
                                                        <td className="px-3 py-2 text-right mono">{p.entry_price}</td>
                                                        <td className="px-3 py-2 text-right mono text-[10px]">{p.stop_loss ?? "—"} / {p.take_profit ?? "—"}</td>
                                                        <td className="px-3 py-2 text-[10px]">{p.entry_ts && new Date(p.entry_ts).toLocaleString("en-KE")}</td>
                                                    </tr>
                                                ))}
                                                {(!data.open_positions || data.open_positions.length === 0) && (
                                                    <EmptyRow colSpan={6} label="No open positions." />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </TabsContent>

                                <TabsContent value="closed">
                                    <div className="border border-border rounded overflow-hidden max-h-[400px] overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-secondary/30 text-muted-foreground sticky top-0">
                                                <tr>
                                                    <SortableTh label="Symbol" sortKey="symbol" currentKey={closedSortKey} direction={closedDirection} onSort={requestClosedSort} className="px-3 py-2" />
                                                    <SortableTh label="Side" sortKey="side" currentKey={closedSortKey} direction={closedDirection} onSort={requestClosedSort} className="px-3 py-2" />
                                                    <th className="text-right px-3 py-2">Entry → Exit</th>
                                                    <SortableTh label={<Jargon term="pnl" />} sortKey="pnl" currentKey={closedSortKey} direction={closedDirection} onSort={requestClosedSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label="Closed" sortKey="closed" currentKey={closedSortKey} direction={closedDirection} onSort={requestClosedSort} className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedClosed.map((p) => {
                                                    const pnl = Number(p.pnl || 0);
                                                    return (
                                                        <tr key={p.id} className="border-t border-border">
                                                            <td className="px-3 py-2 mono font-semibold">{p.symbol}</td>
                                                            <td className={`px-3 py-2 uppercase font-bold ${p.side === "buy" ? "text-success" : "text-danger"}`}>{p.side}</td>
                                                            <td className="px-3 py-2 text-right mono text-[10px]">{p.entry_price} → {p.exit_price}</td>
                                                            <td className={`px-3 py-2 text-right mono ${toneClassName(signTone(pnl))}`}>{fmtKES(pnl)}</td>
                                                            <td className="px-3 py-2 text-[10px]">{p.exit_ts && new Date(p.exit_ts).toLocaleString("en-KE")}</td>
                                                        </tr>
                                                    );
                                                })}
                                                {(!data.closed_trades || data.closed_trades.length === 0) && (
                                                    <EmptyRow colSpan={5} label="No closed trades." />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </TabsContent>

                                <TabsContent value="orders">
                                    <div className="border border-border rounded overflow-hidden max-h-[400px] overflow-y-auto">
                                        <table className="w-full text-xs">
                                            <thead className="bg-secondary/30 text-muted-foreground sticky top-0">
                                                <tr>
                                                    <SortableTh label="Symbol" sortKey="symbol" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} className="px-3 py-2" />
                                                    <SortableTh label="Type" sortKey="type" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} className="px-3 py-2" />
                                                    <SortableTh label="Side" sortKey="side" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} className="px-3 py-2" />
                                                    <SortableTh label="Qty" sortKey="qty" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label="Price" sortKey="price" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} align="right" className="px-3 py-2" />
                                                    <SortableTh label="Status" sortKey="status" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} className="px-3 py-2" />
                                                    <SortableTh label="Timestamp" sortKey="ts" currentKey={orderSortKey} direction={orderDirection} onSort={requestOrderSort} className="px-3 py-2" />
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedOrders.map((o) => (
                                                    <tr key={o.id} className="border-t border-border">
                                                        <td className="px-3 py-2 mono font-semibold">{o.symbol}</td>
                                                        <td className="px-3 py-2 uppercase">{o.order_type || o.type}</td>
                                                        <td className={`px-3 py-2 uppercase ${o.side === "buy" ? "text-success" : "text-danger"}`}>{o.side}</td>
                                                        <td className="px-3 py-2 text-right mono">{o.qty}</td>
                                                        <td className="px-3 py-2 text-right mono">{o.price ?? "market"}</td>
                                                        <td className="px-3 py-2 uppercase text-[10px]">{o.status}</td>
                                                        <td className="px-3 py-2 text-[10px]">{o.ts && new Date(o.ts).toLocaleString("en-KE")}</td>
                                                    </tr>
                                                ))}
                                                {(!data.recent_orders || data.recent_orders.length === 0) && (
                                                    <EmptyRow colSpan={7} label="No orders." />
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </TabsContent>

                                <TabsContent value="notifications">
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                        {data.notifications?.map((n) => (
                                            <div key={n.id} className="border border-border rounded p-2 text-xs bg-card">
                                                <div className="flex justify-between items-baseline mb-1">
                                                    <span className="font-semibold uppercase tracking-wide">{n.category || n.type || "notice"}</span>
                                                    <span className="text-[10px] text-muted-foreground">{n.created_at && new Date(n.created_at).toLocaleString("en-KE")}</span>
                                                </div>
                                                <div className="text-muted-foreground">{n.title || n.message || n.body}</div>
                                            </div>
                                        ))}
                                        {(!data.notifications || data.notifications.length === 0) && (
                                            <div className="p-6 text-center text-muted-foreground text-xs">No notifications.</div>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="journal">
                                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                        {data.journal_entries?.map((j) => (
                                            <div key={j.id} className="border border-border rounded p-2 text-xs bg-card">
                                                <div className="flex justify-between items-baseline mb-1">
                                                    <span className="font-semibold">{j.title || j.symbol || "Entry"}</span>
                                                    <span className="text-[10px] text-muted-foreground">{j.created_at && new Date(j.created_at).toLocaleString("en-KE")}</span>
                                                </div>
                                                <div className="text-muted-foreground whitespace-pre-wrap">{j.note || j.body || j.content}</div>
                                            </div>
                                        ))}
                                        {(!data.journal_entries || data.journal_entries.length === 0) && (
                                            <div className="p-6 text-center text-muted-foreground text-xs">No journal entries.</div>
                                        )}
                                    </div>
                                </TabsContent>

                                <TabsContent value="audit">
                                    {/* Lazy-mount the tab so we only hit /audit-log when the admin
                                        explicitly opens it. */}
                                    <AuditLogView
                                        jsonPath={`/admin/users/${userId}/audit-log`}
                                        csvPath={`/admin/users/${userId}/audit-log.csv`}
                                        subject={userEmail || userId}
                                        defaultDays={90}
                                    />
                                </TabsContent>
                            </Tabs>

                            <div className="mt-4 flex items-center justify-between gap-2">
                                <div className="text-[10px] text-muted-foreground mono">
                                    Generated by {data.generated_by} · {new Date(data.generated_at).toLocaleString("en-KE")}
                                </div>
                                <DialogClose asChild>
                                    <button
                                        type="button"
                                        data-testid="snapshot-close-btn-footer"
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded border border-border hover:bg-secondary text-xs font-semibold transition"
                                    >
                                        <X className="w-3 h-3" /> Close
                                    </button>
                                </DialogClose>
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

// API_BASE re-exported to eliminate an unused-import lint warning if the
// audit-log tab ever bypasses the axios client for a raw fetch download.
void API_BASE;
